import { useFetcher } from "~/hooks/useFetcher";
import { contextSkillsPath, type PartialSkill, type SkillPage } from "~/services/skills";

interface UseConversationSkillsResult {
  skills: PartialSkill[];
  isLoading: boolean;
}

/**
 * Multi-scope skill list for the in-conversation picker.
 *
 * Issues a single `GET /skills/?agent_id=...&dataset_ids=...&user_id=...&unique=true`
 * request and returns the server-deduped union (precedence USER > DATASET >
 * AGENT, resolved server-side via `_dedupe_by_name`). Picker and runtime stay
 * in lockstep because they share the same dedup path.
 *
 * SYSTEM is intentionally absent here — it's a library only and surfaces
 * via the separate "Pick from library" picker.
 */
export function useConversationSkills(
  agentId: string | null | undefined,
  datasetId: string | null | undefined,
  userId: string | null | undefined
): UseConversationSkillsResult {
  const key = contextSkillsPath({
    agent_id: agentId,
    dataset_ids: datasetId ? [datasetId] : null,
    user_id: userId,
  });
  const { data, isLoading } = useFetcher<SkillPage>(key);

  return {
    skills: data?.items ?? [],
    isLoading,
  };
}
